import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import PropTypes from "prop-types";
import ReactDOMServer from "react-dom/server";
import axios from "axios";
import parse from "html-react-parser";
import FilterTags from "./FilterTags";
import CommentTags from "./CommentTags";
import ButtonGroup from "./ButtonGroup";
import { visuallyHidden } from "@mui/utils";
import SummaryRating from "./SummaryRating";
import CustomDropdown from "./CustomDropdown";
import ImportComments from "./ImportComments";
import CLSettings from "./CLSettings";
import AddIcon from "@mui/icons-material/Add";
import Preloader from "../../helpers/Preloader";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { Box, Grid, TextField, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Paper, Button, IconButton, TableHead, TableSortLabel, Typography } from "@mui/material";
import { editNarrativeComment, getCommentLibraryList, updateDropDownValues } from "../../store/action/CommentLibraryAction";
import { stableSort } from "./CommentLibraryHelper";
import FilterButtonGroup from "./FilterButtonGroup";
import AddCommentDialog from "./AddCommentDialog";
import DeleteCommentDialog from "./DeleteCommentDialog";
import "../../../assets/styles/containers/_commentLibrary.scss";
import { AnimatePresence, motion } from "framer-motion";
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import config_settings from "../../config/config";
import CopySummaryModal from "../../common/TemplateUtils/CopySummaryModal";
import uuid from "react-uuid";
import { timestamp } from "../../utils/services";
import { useLocation } from "react-router-dom";

let BASE_URL;

const origin = () => {
  let origin = window.location.origin.toLowerCase();
  if (origin.includes("https://")) origin = origin.replace("https://", "");
  else origin = origin.replace("http://", "");
  BASE_URL = config_settings[origin]?.SERVER_URL;
};

origin();

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index} // Hides the tab panel when it's not selected
      id={`simple-tabpanel-${index}`} // Generates a unique ID for each tab panel
      aria-labelledby={`simple-tab-${index}`} // The ID of the tab
      {...other} // Spreads any other properties
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange, totalPages } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, totalPages - 1);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0}>
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0}>
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton onClick={handleNextButtonClick} disabled={page >= totalPages - 1} >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton onClick={handleLastPageButtonClick} disabled={page >= totalPages - 1}  >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

function TablePaginationActionsHT(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
    onPageChange(event, lastPage);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page" sx={{ ml: 3 }}>
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page" sx={{ ml: 3 }}>
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page" sx={{ ml: 3 }}>
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActionsHT.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
};

function getComparator(order, orderBy) {
  const descendingComparator = (a, b, orderBy) => {
    // Check if orderBy property exists in both objects
    if (!(orderBy in a) || !(orderBy in b)) {
      return 0; // Unable to compare, consider them equal
    }
    // If the property is a string, lowercase both values to compare
    if (typeof a[orderBy] === "string" && typeof b[orderBy] === "string") {
      const lowercaseA = (a[orderBy] || "")
        ?.replace(/<[^>]*>| /g, "")
        ?.replace(/\s/g, "")
        ?.toLowerCase();
      const lowercaseB = (b[orderBy] || "")
        ?.replace(/<[^>]*>| /g, "")
        ?.replace(/\s/g, "")
        ?.toLowerCase();
      if (lowercaseA < lowercaseB) {
        return 1; // Return 1 for ascending order
      } else if (lowercaseA > lowercaseB) {
        return -1; // Return -1 for descending order
      }
    }
    // If the property is a number, compare normally
    if (a[orderBy] < b[orderBy]) {
      return 1; // Return 1 for ascending order
    } else if (a[orderBy] > b[orderBy]) {
      return -1; // Return -1 for descending order
    }
    return 0;
  };
  return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

const CommentLibraryTable = ({ state, setState, GetCommentList, importing, setImporting, tabValue, setProgress }) => {
  const dispatch = useDispatch();
  const storeData = useSelector((state) => state.commentLibrary);
  const isHTCompany = storeData?.isHTCompany
  const { page, search, order, orderBy, selected, expandedRows, commentListData, commentListInitialData, showLoader, filterTagList, selectedFilterTagID, filterType, openAddDialog, } = state;
  const [newlyAddedRows, setNewlyAddedRows] = useState({});
  const [importListing, setImportListing] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [locationsList, setLocationsList] = useState([]);
  const [ratingsList, setRatingsList] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openOption, setOpenOption] = useState(false);
  const openOptionModal = () => setOpenOption(true);
  const closeOptionModal = () => setOpenOption(false);
  const [openCommentImport, setOpenCommentImport] = useState(false);
  const openCommentImportModal = () => setOpenCommentImport(true);
  const closeCommentImportModal = () => setOpenCommentImport(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteRowID, setdeleteROWID] = useState();
  const token = sessionStorage.getItem("token");
  const location = useLocation();

  const STATUS_MAP = {
    all: undefined,
    used: 'used',
    unused: 'unused',
    summary: 'summary',
    deleted: 'deleted',
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((state.searchText || "").trim());
    }, 1000);
    return () => clearTimeout(handle);
  }, [state.searchText]);

  useEffect(() => {
    if (!sessionStorage.getItem("company_id")) return;
    if (storeData?.isHTCompany === true) return;
    const controller = new AbortController();
    async function fetchComments() {
      try {
        setState(p => ({ ...p, showLoader: true }));
        const params = {
          page: state.page + 1,
          rows_per_page: state.rowsPerPage, sort_direction: order, sort_field: orderBy
        };
        const status = STATUS_MAP[state.filterType];
        if (status !== undefined) params.status = status;
        if (debouncedSearch) params.search = debouncedSearch;
        state.selectedFilterTagID.forEach((id, idx) => {
          params[`filter_tags[${idx}]`] = id;
        });
        const res = await axios.get(`${BASE_URL}/comments`, {
          params,
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
        });
        const data = res?.data?.data?.data ?? [];
        const pg = res?.data?.data?.pagination;
        setState(p => ({
          ...p,
          commentListData: Array.isArray(data) ? data : [],
          totalRowCount: pg?.total_count ?? data.length,
          rowsPerPage: pg?.rows_per_page ?? p.rowsPerPage,
          totalPages: pg?.total_pages ?? 1,
          showLoader: false,
        }));
      } catch (err) {
        if (axios.isCancel(err)) return;
        setState(p => ({ ...p, showLoader: false }));
      }
    }
    fetchComments();
  }, [tabValue, storeData?.isHTCompany, state.page, state.rowsPerPage, state.filterType, state.selectedFilterTagID, debouncedSearch, importing, state.refreshTs]);

  useEffect(() => {
    if (!sessionStorage.getItem("company_id")) return;
    if (storeData?.isHTCompany === true) return;
    const controller = new AbortController();
    async function fetchImportListing() {
      try {
        const token = sessionStorage.getItem("token");
        const companyID = sessionStorage.getItem("company_id");
        const res = await axios.get(`${BASE_URL}/templates`, {
          params: {
            company_id: companyID,
          },
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
        });
        setImportListing(res?.data?.data);
      } catch (err) {
        if (axios.isCancel(err)) return;
      }
    }
    fetchImportListing();
    return () => controller.abort();
  }, [state?.commentListData, storeData?.isHTCompany]);

  // ---------------- FETCH LOCATIONS (for Location Autocomplete) ----------------
  useEffect(() => {
    if (!sessionStorage.getItem("company_id")) return;
    const controller = new AbortController();
    async function fetchLocations() {
      try {
        const companyID = sessionStorage.getItem("company_id");
        const res = await axios.get(`${BASE_URL}/locations`, {
          // params: { company_id: companyID },
          signal: controller.signal,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        const data = res?.data?.data ?? [];
        setLocationsList(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!axios.isCancel(err)) console.error("LOCATIONS API error:", err);
      }
    }
    fetchLocations();
    return () => controller.abort();
  }, [sessionStorage.getItem("company_id")]);

  // ---------------- FETCH RATINGS (for Rating Autocomplete) ----------------
  useEffect(() => {
    if (!sessionStorage.getItem("company_id")) return;
    const controller = new AbortController();
    async function fetchRatings() {
      try {
        const companyID = sessionStorage.getItem("company_id");
        const res = await axios.get(`${BASE_URL}/ratings`, {
          params: { company_id: companyID },
          signal: controller.signal,
          headers: { "Authorization": `Bearer ${token}` },
        });
        const data = res?.data?.data ?? [];
        let orderedData = Array.isArray(data) ? [...data].reverse() : [];
        // Apply the same recently-edited reordering used by the Ratings tab
        // so the dropdown order matches what the user sees in the Ratings table.
        try {
          const storageKey = `ratings_recently_edited_${companyID}`;
          const recentIds = JSON.parse(sessionStorage.getItem(storageKey) || "[]");
          if (Array.isArray(recentIds) && recentIds.length > 0) {
            const existingIds = new Set(orderedData.map((item) => item.id));
            const cleanedIds = recentIds.filter((id) => existingIds.has(id));
            cleanedIds.forEach((id, i) => {
              const idx = orderedData.findIndex((item) => item.id === id);
              if (idx !== -1 && idx !== i) {
                const [item] = orderedData.splice(idx, 1);
                orderedData.splice(i, 0, item);
              }
            });
          }
        } catch (_) { /* ignore sessionStorage parse errors */ }
        setRatingsList(orderedData);
      } catch (err) {
        if (!axios.isCancel(err)) console.error("RATINGS API error:", err);
      }
    }
    fetchRatings();
    return () => controller.abort();
  }, [sessionStorage.getItem("company_id")]);
  // ---------------- FETCH TAGS ----------------
  useEffect(() => {
    if (!sessionStorage.getItem("company_id")) return;
    const controller = new AbortController();
    async function fetchTags() {
      try {
        const companyID = sessionStorage.getItem("company_id");
        const res = await axios.get(`${BASE_URL}/filter-tag-list`, {
          // params: { company_id: companyID },
          signal: controller.signal,
          headers: { "Authorization": `Bearer ${token}` },
        });
        setTagsList(res?.data?.data);
      } catch (err) {
        if (!axios.isCancel(err)) console.error("TAGS API error:", err);
      }
    }
    if(window.location.pathname.includes("/templates")){
      if (openAddDialog) fetchTags();
    }
    else {
     fetchTags();
    }
    return () => controller.abort();
  }, [sessionStorage.getItem("company_id"), openAddDialog, window.location.pathname]);

  useEffect(() => {
    if (storeData?.isHTCompany) {
      async function callApi() {
        await dispatch(getCommentLibraryList(1, null, { page: 1, filterByTagId: selectedFilterTagID, filterByType: filterType }));
      }
      if (!storeData?.commentLibraryList) {
        setState((prevState) => ({ ...prevState, isLoading: true }));
        callApi();
        setState((prevState) => ({ ...prevState, value: 0, isLoading: false }));
      }
      if (storeData?.totalListCount && !storeData.commentLibraryWholeList) {
        dispatch(getCommentLibraryList(null, storeData?.totalListCount));
      }
    }
  }, [storeData?.totalListCount, state.refreshTs, storeData?.commentLibraryList, dispatch, storeData?.isHTCompany]);

  useEffect(() => {
    if (storeData?.isHTCompany) {
      if (storeData.commentLibraryWholeList && !commentListInitialData) {
        setState((prevState) => ({ ...prevState, commentListInitialData: storeData.commentLibraryWholeList || [] }));
      }
      if (storeData.commentLibraryList && !commentListData) {
        setState((prevState) => ({ ...prevState, commentListData: storeData.commentLibraryList || [] }));
        dispatch(getCommentLibraryList(1));
      }
    }
  }, [storeData.commentLibraryWholeList, storeData?.commentLibraryList, storeData?.isHTCompany]);

  useEffect(() => {
    if (!filterTagList && storeData?.clTagList) setState((prevState) => ({ ...prevState, filterTagList: storeData?.clTagList || [] }));
  }, [filterTagList, storeData?.clTagList]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      commentListData: storeData.commentLibraryList || prevState.commentListData,
      page: 0
    }));
  }, [storeData?.commentLibraryList]);

  const EnhancedTableHead = (props) => {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };
    return (
      <TableHead>
        <TableRow>
          {headCells.map((headCell) => (
            <TableCell key={headCell.id} align={"left"} padding={headCell.disablePadding ? "none" : "normal"} sortDirection={orderBy === headCell.id ? order : false} className="cl-table-head">
              <TableSortLabel
                active={orderBy === headCell.id}
                style={{ cursor: headCell.id === "rating" ? "default" : "pointer" }}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={(headCell.id === "rating" || headCell.id === "expand_icon") ? null : createSortHandler(headCell.id)}
                IconComponent={() => (headCell.id !== "expand_icon" && headCell.id !== "global" && headCell.id !== "rating" ? <UnfoldMoreIcon className="cl-sort-icon" /> : "")}
              >
                {state?.filterType == "deleted" && headCell.label == "Last modified" ? "Date deleted" : headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
  };

  const headCells = [
    { id: "expand_icon", numeric: false, disablePadding: true, label: <LastPageIcon className="cl-table-exp-icon" onClick={toggleAllRowsExpansion} /> },
    { id: "title", numeric: false, disablePadding: false, label: "Title" },
    { id: isHTCompany ? "comment_text" : "comment", numeric: false, disablePadding: false, label: "Comment" },
    { id: isHTCompany ? "summary_comment_text" : "rating", numeric: false, disablePadding: false, label: isHTCompany ? "Summary" : "Rating" },
    { id: "times_used", numeric: false, disablePadding: false, label: "Times used" },
    { id: "updated_at", numeric: false, disablePadding: false, label: "Last modified" },
    { id: "global", numeric: false, disablePadding: true, label: "" },
  ];

  const toggleRowExpansion = (rowId) => {
    setState((prevState) => ({ ...prevState, expandedRows: expandedRows.includes(rowId) ? expandedRows.filter((id) => id !== rowId) : [...expandedRows, rowId] }));
  };

  const isRowExpanded = (rowId) => expandedRows.includes(rowId);

  function toggleAllRowsExpansion() {
    const allRowIds = commentListData.map((row) => row.id);
    setState((prevState) => ({ ...prevState, expandedRows: expandedRows.length === allRowIds.length ? [] : allRowIds }));
  }

  const handleRequestSort = (event, property) => {
    const isAsc = state.orderBy === property && state.order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setState((prevState) => ({
      ...prevState,
      order: newOrder,
      orderBy: property,
      page: 0,
      refreshTs: Date.now(),
    }));
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const selectedComment = commentListData.map((n) => n.id);
      setState((prevState) => ({ ...prevState, selected: selectedComment }));
      return;
    }
    setState((prevState) => ({ ...prevState, selected: [] }));
  };

  function replaceDropdownsWithSelects(htmlString, dropdownValues, type, comment_id, comment_uuid) {
    var uuidPattern = /\[dropdown ([a-f0-9-]+)\]/g;
    var uuids = htmlString?.match(uuidPattern);
    let singleUuidPattern = /\[dropdown ([a-f0-9-]+)\]/;
    const regex = /Â/g;
    if (uuids) {
      uuids?.forEach(function (uuid) {
        let uuidSingle = uuid.match(singleUuidPattern)[1];
        let commentDropValues = dropdownValues?.find((el) => el.dropdown_uuid === uuidSingle);
        let newDropdownValues = commentDropValues?.dropdowns_values || [];
        newDropdownValues = Array.isArray(newDropdownValues) && newDropdownValues?.filter((obj) => !obj?.toUpperCase()?.includes("XXXXX"));
        if (Array.isArray(newDropdownValues) && !newDropdownValues?.map((value) => value?.toUpperCase())?.some((value) => value?.includes("------"))) {
          newDropdownValues?.unshift("-------------");
        }
        var selectHtml = React.createElement(CustomDropdown, {
          dropdownUuid: uuidSingle || "",
          commentUuid: comment_uuid || "",
          commentId: comment_id || "",
          type: type || "",
          options: newDropdownValues || [],
          selectedValue: commentDropValues?.selected_value || "",
        });
        htmlString = htmlString?.replace(uuid, ReactDOMServer.renderToStaticMarkup(selectHtml));
      });
    } else {
      var parser = new DOMParser();
      var doc = parser.parseFromString(htmlString, "text/html");
      var selectTags = doc.querySelectorAll("select");
      selectTags.forEach((select) => {
        let uuidSingle = select.getAttribute("id");
        let commentDropValues = dropdownValues?.find((el) => el.dropdown_uuid === uuidSingle);
        let newDropdownValues = commentDropValues?.dropdowns_values || [];
        newDropdownValues = Array.isArray(newDropdownValues) && newDropdownValues?.filter((obj) => !obj?.toUpperCase()?.includes("XXXXX"));
        if (Array.isArray(newDropdownValues) && !newDropdownValues?.map((value) => value?.toUpperCase())?.some((value) => value?.includes("------"))) {
          newDropdownValues?.unshift("-------------");
        }
        var selectHtml = React.createElement(CustomDropdown, {
          dropdownUuid: uuidSingle || "",
          commentUuid: comment_uuid || "",
          commentId: comment_id || "",
          type: type || "",
          options: newDropdownValues || [],
          selectedValue: commentDropValues?.selected_value || "",
        });
        let selectOuterHtml = select.outerHTML;
        htmlString = htmlString.replace(selectOuterHtml, ReactDOMServer.renderToStaticMarkup(selectHtml));
      });
    }
    const replaceInputs = (input_text) => {
      let container = document.createElement("div");
      container.innerHTML = input_text;
      container.innerHTML = container.innerHTML.replace(/\[textbox (.*?)\]/g, (match, attributes) => {
        let idMatch = attributes.match(/id:([^;]+);?/);
        let valueMatch = attributes.match(/value:([^;]+);?/);
        let inputId = idMatch && idMatch[1].length > 30 ? idMatch[1].trim() : uuid();
        let value = valueMatch ? valueMatch[1].trim() : "";
        const safeValue = value.replace(/\\/g, "\\\\").replace(/"/g, "&quot;");
        return `<input id="${inputId}" value="${safeValue}" comment_id="${comment_id}" class="${type}-cl-input" title="${safeValue}" style="width: 104px;
                background-color: #f1f9fe;
                height: 24px;
                overflow-wrap: break-word;
                padding: 1px 17px 3px 6px;
                text-overflow: ellipsis;
                white-space: nowrap;
                border-radius: 4px;
                border: 1px solid #0F75BC;
                appearance: none;
                -webkit-appearance: none;
                -moz-appearance: none;
                font-size: 14px;
                color: #3B3B3B;
                font-style: normal;
                font-weight: 600;
                max-width: 200px;
                min-width: 104px;"
                onblur="handleChangeInput(inputId, value)"
                />`;
      });
      return container.innerHTML;
    }
    return parse(replaceInputs(htmlString?.replace(regex, "")?.replace(/Ã/g, "")));
  }

  if (!openAddDialog) {
    let commentDropElements = document.querySelectorAll(".comment-cl-dropdown");
    commentDropElements?.forEach((element) => {
      const commentId = element.getAttribute("comment_id");
      const dropType = element.getAttribute("dropType");
      const commentUUId = element.getAttribute("comment_uuid");
      const dropdownUUID = element.getAttribute("dropdown_uuid");
      const commentDisabled = element.getAttribute("disabled");
      if (element !== null && element !== undefined) {
        element.onchange = (event) => handleChangeDropdown(event, dropdownUUID, commentId, dropType, commentUUId, commentDisabled);
        element.onclick = (event) => event.stopPropagation();
      }
    });
    let summaryDropElements = document.querySelectorAll(".summary-cl-dropdown");
    summaryDropElements?.forEach((element) => {
      const commentId = element.getAttribute("comment_id");
      const dropType = element.getAttribute("dropType");
      const commentUUId = element.getAttribute("comment_uuid");
      const dropdownUUID = element.getAttribute("dropdown_uuid");
      const commentDisabled = element.getAttribute("disabled");
      if (element !== null && element !== undefined) {
        element.onchange = (event) => handleChangeDropdown(event, dropdownUUID, commentId, dropType, commentUUId, commentDisabled);
        element.onclick = (event) => event.stopPropagation();
      }
    });

    let commentInputElements = document.querySelectorAll(".comment-cl-input");
    commentInputElements?.forEach((element) => {
      if (element !== null && element !== undefined) {
        const commentId = element.getAttribute("comment_id");
        element.onclick = (event) => {
          event.stopPropagation();
        };
        element.onblur = async (event) => {
          const newUpdatedList = commentListData;
          const commentIndex = commentListData.findIndex((item) => item.id === parseInt(commentId));
          const targetId = event.target.id;
          const newValue = event.target.value;
          let commentText = isHTCompany ? commentListData[commentIndex]?.comment_text : commentListData[commentIndex]?.comment;
          const textboxPattern = new RegExp(`\\[textbox id:${targetId};(.*?)]`, 'g');
          commentText = commentText?.replace(textboxPattern, (match, content) => {
            if (/value:.*?;?/.test(content)) {
              return match.replace(/value:([\s\S]*?);(?=\s*\])/i, `value:${newValue};`);
            } else {
              return match.replace(/\]$/, ` value:${newValue}; ]`);
            }
          });
          let newCommentData = commentListData;
          newCommentData[commentIndex] = {
            ...commentListData[commentIndex], comment_text: commentText, comment_id: parseInt(commentId),
            tag_ids: (commentListData[commentIndex]?.tags || []).map((t) => t?.pivot?.tag_id ?? t?.tag_id ?? t.id),
            rating_ids: (commentListData[commentIndex]?.ratings || []).map((r) => r?.pivot?.rating_id ?? r?.rating_id ?? r.id),
            summary_ids: (commentListData[commentIndex]?.summaries || []).map((s) => s?.pivot?.summary_id ?? s?.summary_id ?? s.id),
            updated_at: timestamp()
          }
          if (isHTCompany) await dispatch(editNarrativeComment(newCommentData[commentIndex]));
          else {
            const companyID = sessionStorage.getItem("company_id");
            await axios.put(
              `${BASE_URL}/comments/${parseInt(commentId)}`,
              {
                comment_dropdowns: commentListData[commentIndex]?.comment_dropdowns,
                location: commentListData[commentIndex]?.location,
                title: commentListData[commentIndex]?.title,
                tag_ids: (commentListData[commentIndex]?.tags || []).map((t) => t?.pivot?.tag_id ?? t?.tag_id ?? t.id),
                rating_ids: (commentListData[commentIndex]?.ratings || []).map((r) => r?.pivot?.rating_id ?? r?.rating_id ?? r.id),
                comment: commentText, updated_at: timestamp()
              },
              { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
            );
          }
          setState((prevState) => ({ ...prevState, showLoader: false, commentListData: newUpdatedList }));
        }
      }
    });

    let summaryInputElements = document.querySelectorAll(".summary-cl-input");
    summaryInputElements?.forEach((element) => {
      if (element !== null && element !== undefined) {
        const commentId = element.getAttribute("comment_id");
        element.onclick = (event) => {
          event.stopPropagation();
        };
        element.onblur = async (event) => {
          const newUpdatedList = commentListData;
          const commentIndex = commentListData.findIndex((item) => item.id === parseInt(commentId));
          const targetId = event.target.id;
          const newValue = event.target.value;
          let summaryText = commentListData[commentIndex]?.summary_comment_text;
          const textboxPattern = new RegExp(`\\[textbox id:${targetId};(.*?)]`, 'g');
          summaryText = summaryText?.replace(textboxPattern, (match, content) => {
            if (/value:.*?;?/.test(content)) {
              return match.replace(/value:([\s\S]*?);(?=\s*\])/i, `value:${newValue};`);
            } else {
              return match.replace(/\]$/, ` value:${newValue}; ]`);
            }
          });
          let newCommentData = commentListData;
          newCommentData[commentIndex] = {
            ...commentListData[commentIndex], summary_comment_text: summaryText, comment_id: parseInt(commentId),
            tag_ids: (commentListData[commentIndex]?.tags || []).map((t) => t?.pivot?.tag_id ?? t?.tag_id ?? t.id),
            rating_ids: (commentListData[commentIndex]?.ratings || []).map((r) => r?.pivot?.rating_id ?? r?.rating_id ?? r.id),
            summary_ids: (commentListData[commentIndex]?.summaries || []).map((s) => s?.pivot?.summary_id ?? s?.summary_id ?? s.id),
            updated_at: timestamp()
          }
          await dispatch(editNarrativeComment(newCommentData[commentIndex]));
          setState((prevState) => ({ ...prevState, showLoader: false, commentListData: newUpdatedList }));
        }
      }
    });
  }

  const handleChangeRowsPerPage = (e) => {
    const newSize = parseInt(e.target.value, 10);
    sessionStorage.setItem("commentsRowPerPage", newSize);
    setState(p => ({
      ...p,
      rowsPerPage: newSize,
      page: 0,
      expandedRows: [],
    }));
  };
  const handleClickChangeDropdown = (event, id, row, action) => {
    !isRowExpanded(id) && toggleRowExpansion(row.id);
    const selectedIndex = selected.indexOf(row.uuid);
    if (action === "dropdown") {
      if (!isSelected(row.uuid)) {
        let newSelected = [];
        if (selectedIndex === -1) {
          newSelected = newSelected.concat(selected, row.uuid);
        } else if (selectedIndex === 0) {
          newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
          newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
          newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setState((prevState) => ({ ...prevState, selected: newSelected }));
      }
    }
  };

  const handleChangeDropdown = async (event, dropdownUuid, commentId, type, commentUUId, commentDisabled) => {
    event.stopPropagation();
    commentDisabled === "false" && handleClickChangeDropdown(event, commentId, { id: commentId, uuid: commentUUId }, "dropdown");
    const selectedValue = event.target.value;
    const dropdownValuesArrays = commentListData.map((item) => {
      const commentDropdowns = item.comment_dropdowns || [];
      const summaryDropdowns = item.summary_comment_dropdowns || [];
      const allDropdowns = [...commentDropdowns, ...summaryDropdowns];
      return allDropdowns.filter((dropdown) => dropdown.dropdown_uuid === dropdownUuid).map((dropdown) => dropdown.dropdowns_values);
    });
    const dropdownValues = dropdownValuesArrays.flat().find((values) => values.length > 0);
    let payload = {
      comment_id: commentId,
      dropdown_uuid: dropdownUuid,
      dropdown_type: type === "comment" ? "1" : "2",
      selected_value: selectedValue,
      dropdowns_values: dropdownValues,
    };
    if (!storeData?.isHTCompany) {
      await updateDropDownNHT(payload);
    }
    else {
      await dispatch(updateDropDownValues(payload));
    }
  };

  const updateDropDownNHT = async (payload) => {
    if (!isHTCompany) {
      try {
        await axios.put(
          `${BASE_URL}/update-nn-comment-dropdown`,
          { ...payload },
          {
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
          }
        );
        setState((prev) => ({ ...prev, page: 0, refreshTs: Date.now(), }));
      } catch (err) {
        setState((prev) => ({ ...prev, showLoader: false }));
      }
      return;
    }
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
    setState((prevState) => ({ ...prevState, page: newPage, expandedRows: [] }));
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const visibleRows = React.useMemo(() => {
    return stableSort(commentListData, getComparator(order, orderBy), newlyAddedRows, currentPage);
  }, [commentListData, order, orderBy, newlyAddedRows, currentPage, state.refreshTs]); // Added refreshTs dependency

  const hasActiveFilters = !!debouncedSearch || (Array.isArray(state.selectedFilterTagID) && state.selectedFilterTagID.length > 0) || (state.filterType && state.filterType !== "all");

  const handleSearchFilter = (value) => {
    setState((prev) => ({ ...prev, searchText: value, page: 0 }));
  };

  const handleDelete = async (row) => {
    if (!isHTCompany) {
      try {
        setState((prev) => ({ ...prev, showLoader: true }));
        await axios.delete(`${BASE_URL}/comments/${row?.id}`, {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        setState((prev) => ({ ...prev, showLoader: false, page: 0, refreshTs: Date.now() }));
        setdeleteROWID();
        setDeleteConfirm(false)
      } catch (err) {
        setState((prev) => ({ ...prev, showLoader: false }));
        setdeleteROWID();
        setDeleteConfirm(false)
      }
      return;
    }
    setState((prevState) => ({ ...prevState, openDeleteDialog: true, comment_id: row?.id }));
  };

  return (
    <>
      <AddCommentDialog
        state={state}
        setState={setState}
        tagOptions={isHTCompany ? filterTagList : tagsList}
        locationOptions={locationsList}
        ratingOptions={ratingsList}
      />
      <CopySummaryModal
        open={deleteConfirm}
        handleClose={() => setDeleteConfirm(false)}
        handleSubmit={() => handleDelete(deleteRowID)}
        submitButtonTitle={"Delete"}
        dialogTitle={`Delete ${deleteRowID?.name || ""}`}
        dialogContentText={"Are you sure you want to delete this comment from the comment library?"}
      />
      <DeleteCommentDialog state={state} setState={setState} />
      <CLSettings openCommentImportModal={openCommentImportModal} open={openOption} onClose={closeOptionModal} />
      <ImportComments closeOptionModal={closeOptionModal} importing={importing} setImporting={setImporting} setProgress={setProgress} open={openCommentImport} onClose={closeCommentImportModal} data={importListing} />
      <Preloader showPreloader={showLoader} />
      <Box sx={{ width: "100%", paddingX: "30px" }}>
        <Grid container className="cl-search-container">
          <Grid item xs={6} md={6} lg={2} className="cl-search-bar">
            <div className="cl-search">
              <TextField
                id="search-bar"
                variant="outlined"
                placeholder="Search in table"
                size="small"
                onChange={(e) => {
                  if (storeData?.isHTCompany) {
                    handleSearchFilter(e.target.value);
                  } else {
                    setState((p) => ({
                      ...p,
                      searchText: e.target.value,
                      page: 0,
                    }));
                  }
                }}
              />
              <SearchIcon className="cl-search-icon" />
            </div>
          </Grid>
          <Grid item xs={6} md={6} lg={3}>
            <FilterTags filterTagList={isHTCompany ? filterTagList : tagsList} GetCommentList={GetCommentList} filterType={filterType} setState={setState} state={state} />
          </Grid>
          <Grid item xs={4} md={6} lg={isHTCompany ? 3.5 : 3}>
            <FilterButtonGroup GetCommentList={GetCommentList}
              selectedFilterTagID={state.selectedFilterTagID}
              setState={setState} isHTCompany={isHTCompany} />
          </Grid>
          <Grid item xs={8} md={6} lg={isHTCompany ? 3.5 : 3} sx={{ gap: "5px" }}>
            <Button startIcon={<AddIcon />} className="create-new-btn" onClick={() => setState((prevState) => ({ ...prevState, openAddDialog: true }))}>
              <span>Create new</span>
            </Button>
          </Grid>
          {!isHTCompany ? <Grid item xs={12} md={1.5} lg={1} sx={{ my: { xs: 1, sm: 1, md: 1, lg: 0 } }}>
            <Button onClick={openOptionModal} startIcon={<SettingsOutlinedIcon sx={{ color: "#454545" }} />} variant="contained" className="options-btn">
              Options
            </Button>
          </Grid> : null}
        </Grid>
        <Paper sx={{ width: "100%", mb: 2, border: "1px solid #D5D5D5", borderRadius: "3px" }} className="cl-table" elevation={0}>
          <TableContainer className="cl-table-container" sx={{ overflowY: visibleRows?.length > 0 ? "auto" : "hidden !important" }}>
            <Table stickyHeader size={"medium"}>
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={commentListData?.length}
              />
              <AnimatePresence>
                <TableBody>
                  {visibleRows?.length > 0 ? visibleRows?.map((row, index) => {
                    const isNewlyAdded = newlyAddedRows[row.id];
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <TableRow
                        key={`${row?.id}-${state.refreshTs}`}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                        hover
                        className={isNewlyAdded ? "new-row" : ""}
                        aria-checked={isItemSelected}
                        tabIndex={1}
                        selected={isItemSelected}
                        sx={{ cursor: "pointer", borderLeft: "1px solid blue" }}
                      >
                        <TableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          padding="none"
                          className="cl-exp-btn cl-exp-col"
                          sx={{ borderLeft: `${isRowExpanded(row.id) ? "1px solid #0062A0" : "none"}` }}
                        >
                          <IconButton onClick={() => toggleRowExpansion(row.id)} style={{ marginLeft: "15px" }}>
                            {isRowExpanded(row.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell className="cl-title-col" align="left">
                          <div style={{ width: "200px" }} className={`cl-comment-title ${isRowExpanded(row.id) ? "expanded" : ""}`}>{row?.title}</div>
                        </TableCell>
                        <TableCell className="cl-comment-col" align="left">
                          <div className={`comment-text ${isRowExpanded(row.id) ? "expanded" : ""}`} onClick={() => (!isRowExpanded(row.id) ? toggleRowExpansion(row.id) : undefined)}>
                            {isHTCompany ? row?.comment_text !== null && replaceDropdownsWithSelects(row?.comment_text?.replace(/<\/?[^>]+(>|$)/g, ""), row?.comment_dropdowns, "comment", row.id, row.uuid) : row?.comment !== null && replaceDropdownsWithSelects(row?.comment?.replace(/<\/?[^>]+(>|$)/g, ""), row?.comment_dropdowns, "comment", row.id, row.uuid)}
                          </div>
                          {row?.tags?.length ? <CommentTags tags={row?.tags} /> : null}
                        </TableCell>
                        <TableCell className="cl-summary-col" align="left">
                          {isHTCompany ? <div className={`summary-text ${isRowExpanded(row.id) ? "expanded" : ""}`}>
                            {row?.summary_comment_text !== null &&
                              replaceDropdownsWithSelects(row?.summary_comment_text?.replace(/<\/?[^>]+(>|$)/g, ""), row?.summary_comment_dropdowns, "summary", row.id, row.uuid)}
                          </div> : null}
                          {isHTCompany ? <SummaryRating rating={row?.summaries} /> : <SummaryRating rating={row?.ratings} />}
                        </TableCell>
                        <TableCell className="cl-rating-col" align="left">
                          {row?.times_used}
                        </TableCell>
                        <TableCell className="cl-global-col" align="left" style={{ width: "200px", position: "relative" }}>
                          <span className="date-placeholder-comment">{moment(row?.updated_at)?.format("MM/DD/YYYY")}</span>
                          <span className="time-placeholder">{moment(row?.updated_at)?.format("LT")}</span>
                        </TableCell>
                        <TableCell align="right" width="160px" sx={{ position: "relative" }}>
                          <ButtonGroup setdeleteROWID={setdeleteROWID} setDeleteConfirm={setDeleteConfirm} isHTCompany={isHTCompany} state={state} row={row} setState={setState} GetCommentList={GetCommentList} setNewlyAddedRows={setNewlyAddedRows} />
                        </TableCell>
                      </TableRow>
                    );
                  }) : <TableRow style={{ height: "100%" }}>
                    <TableCell colSpan={999} style={{ padding: 0 }}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        height="100%"
                        minHeight="360px" // force some height if table has no rows
                        textAlign="center"
                      >
                        <Typography className="cl-empty-text">{hasActiveFilters ? "No results found" : "Comment library is empty"}</Typography>
                        {!hasActiveFilters && (
                          <span >
                            <Typography onClick={openCommentImportModal} className="cl-import-text">Import comments from templates</Typography>
                          </span>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>}
                </TableBody>
              </AnimatePresence>
            </Table>
          </TableContainer>
        </Paper>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 200]}
          component="div"
          count={state.totalRowCount || 0}
          rowsPerPage={state.rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={
            !storeData?.isHTCompany
              ? (p) => <TablePaginationActions {...p} totalPages={state.totalPages} />
              : TablePaginationActionsHT
          }
        />
      </Box>
    </>
  );
};

export default CommentLibraryTable;
